<template>
    <UserLayout :page-title="t('referral')" :page-section="t('network')">
        <div class="rf-wrap">
            <div class="rf-inner">

                <!-- Welcome Strip -->
                <div class="rf-welcome anim-up" style="--d:0ms">
                    <div class="rw-left">
                        <div class="rw-icon">
                            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                            </svg>
                        </div>
                        <div>
                            <p class="rw-eyebrow">{{ t('network') }}</p>
                            <h1 class="rw-title">{{ t('referral') }}</h1>
                        </div>
                    </div>
                    <div class="rw-chips">
                        <div class="rw-chip">
                            <span class="rw-chip-val">{{ currencySymbol }}{{ formatCurrency(referralStats.totalEarned) }}</span>
                            <span class="rw-chip-lbl">{{ t('totalEarned') }}</span>
                        </div>
                        <div class="rw-chip rw-chip--green">
                            <span class="rw-chip-val">{{ referralStats.activeReferrals }}</span>
                            <span class="rw-chip-lbl">{{ t('active') }}</span>
                        </div>
                        <div class="rw-chip rw-chip--amber">
                            <span class="rw-chip-val">+{{ calculateGrowth() }}%</span>
                            <span class="rw-chip-lbl">{{ t('growth') }}</span>
                        </div>
                    </div>
                </div>

                <!-- Stat Cards -->
                <div class="rf-stats">
                    <div class="rf-stat anim-up" style="--d:60ms;--ac:167,93,237">
                        <div class="rst-glow"></div>
                        <div class="rst-icon">
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                            </svg>
                        </div>
                        <p class="rst-lbl">{{ t('totalReferrals') }}</p>
                        <p class="rst-val">{{ referralStats.totalReferrals }}</p>
                        <p class="rst-sub">{{ referralStats.activeReferrals }} {{ t('active') }}</p>
                        <div class="rst-bar-wrap"><div class="rst-bar shimmer-bar" style="width:100%"></div></div>
                    </div>

                    <div class="rf-stat anim-up" style="--d:100ms;--ac:52,211,153">
                        <div class="rst-glow"></div>
                        <div class="rst-icon">
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                        </div>
                        <p class="rst-lbl">{{ t('totalEarned') }}</p>
                        <p class="rst-val">{{ currencySymbol }}{{ formatCurrency(referralStats.totalEarned) }}</p>
                        <p class="rst-sub">+{{ calculateGrowth() }}% {{ t('thisMonth') }}</p>
                        <div class="rst-bar-wrap"><div class="rst-bar shimmer-bar" style="width:72%"></div></div>
                    </div>

                    <div class="rf-stat anim-up" style="--d:140ms;--ac:251,191,36">
                        <div class="rst-glow"></div>
                        <div class="rst-icon">
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                        </div>
                        <p class="rst-lbl">{{ t('pendingCommission') }}</p>
                        <p class="rst-val">{{ currencySymbol }}{{ formatCurrency(referralStats.pendingCommission) }}</p>
                        <p class="rst-sub">{{ t('awaiting') }}</p>
                        <div class="rst-bar-wrap"><div class="rst-bar shimmer-bar" style="width:45%"></div></div>
                    </div>

                    <div class="rf-stat anim-up" style="--d:180ms;--ac:59,130,246">
                        <div class="rst-glow"></div>
                        <div class="rst-icon">
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                            </svg>
                        </div>
                        <p class="rst-lbl">{{ t('teamInvestment') }}</p>
                        <p class="rst-val">{{ currencySymbol }}{{ formatCurrency(referralStats.teamInvestment) }}</p>
                        <p class="rst-sub">{{ referralStats.totalReferrals }} {{ t('members') }}</p>
                        <div class="rst-bar-wrap"><div class="rst-bar shimmer-bar" style="width:88%"></div></div>
                    </div>
                </div>

                <!-- Main 2-col -->
                <div class="rf-main anim-up" style="--d:240ms">
                    <div class="rf-left">
                        <!-- Referral Link -->
                        <div class="rf-card">
                            <div class="rfc-head">
                                <div>
                                    <h3 class="rfc-title">{{ t('yourReferralLink') }}</h3>
                                    <p class="rfc-sub">{{ t('shareThisLink') }}</p>
                                </div>
                                <Link href="/user/referral-level" class="rfc-pill-btn">
                                    {{ t('viewLevel') }}
                                    <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"/>
                                    </svg>
                                </Link>
                            </div>
                            <div class="rfc-link-row">
                                <input type="text" :value="referralLink" readonly class="rfc-input" @click="selectText"/>
                                <button @click="copyToClipboard" class="rfc-copy-btn">
                                    <svg v-if="!copied" width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                                    </svg>
                                    <svg v-else width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
                                    </svg>
                                    {{ copied ? t('copied') : t('copy') }}
                                </button>
                            </div>
                        </div>

                        <!-- Commission Tiers -->
                        <div class="rf-card">
                            <h3 class="rfc-title" style="margin-bottom:4px">{{ t('commissionStructure') }}</h3>
                            <p class="rfc-sub" style="margin-bottom:16px">{{ t('earnUpToLevels') }}</p>
                            <div class="rfc-tiers">
                                <div v-for="tier in commissionTiers" :key="tier.level" class="rfc-tier">
                                    <div class="rfc-tier-rate">{{ tier.commission_rate }}%</div>
                                    <div class="rfc-tier-lvl">{{ t('level') }} {{ tier.level }}</div>
                                    <div class="rfc-tier-type">{{ tier.applies_to }}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="rf-right">
                        <!-- Network Overview -->
                        <div class="rf-card rf-card--stretch">
                            <div class="rfc-head">
                                <h3 class="rfc-title">{{ t('networkOverview') }}</h3>
                                <Link href="/user/referral-network" class="rfc-pill-btn">
                                    {{ t('viewNetwork') }}
                                    <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"/>
                                    </svg>
                                </Link>
                            </div>

                            <div class="rfc-mini-stats">
                                <div class="rfc-mini-stat">
                                    <p class="rfc-mini-val">{{ referralStats.totalReferrals }}</p>
                                    <p class="rfc-mini-lbl">{{ t('total') }}</p>
                                </div>
                                <div class="rfc-mini-stat">
                                    <p class="rfc-mini-val">{{ referralStats.activeReferrals }}</p>
                                    <p class="rfc-mini-lbl">{{ t('active') }}</p>
                                </div>
                            </div>

                            <div class="rfc-prog-block">
                                <div class="rfc-prog-head">
                                    <span class="rfc-prog-lbl">{{ t('commissionProgress') }}</span>
                                    <span class="rfc-prog-earned">{{ currencySymbol }}{{ formatCurrency(referralStats.totalEarned) }}</span>
                                </div>
                                <div class="rfc-prog-track">
                                    <div class="rfc-prog-fill shimmer-bar" :style="`width:${progressPercentage}%`"></div>
                                </div>
                                <div class="rfc-prog-marks">
                                    <span>{{ currencySymbol }}0</span>
                                    <span>{{ currencySymbol }}{{ formatNumber(progressGoal) }} {{ t('goal') }}</span>
                                </div>
                            </div>

                            <div class="rfc-code-block">
                                <p class="rfc-code-label">{{ t('referralCode') }}</p>
                                <div class="rfc-code-row">
                                    <code class="rfc-code">{{ referralCode }}</code>
                                    <button @click="copyReferralCode" class="rfc-code-btn">
                                        <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Bottom Row -->
                <div class="rf-bottom anim-up" style="--d:340ms">
                    <!-- Recent Commissions -->
                    <div class="rf-card">
                        <div class="rfc-head">
                            <div>
                                <h3 class="rfc-title">{{ t('recentCommissions') }}</h3>
                                <p class="rfc-sub">{{ t('latestReferralEarnings') }}</p>
                            </div>
                            <Link href="/user/referral-commissions" class="rfc-pill-btn">
                                {{ t('viewAll') }}
                                <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"/>
                                </svg>
                            </Link>
                        </div>
                        <div class="rfc-list">
                            <div v-for="commission in recentCommissions" :key="commission.id" class="rfc-list-item">
                                <div class="rfc-li-icon">
                                    <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                    </svg>
                                </div>
                                <div class="rfc-li-info">
                                    <p class="rfc-li-name">{{ commission.referrer_name }}</p>
                                    <p class="rfc-li-meta">{{ commission.type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) }}</p>
                                </div>
                                <div class="rfc-li-right">
                                    <p class="rfc-li-amount">+{{ currencySymbol }}{{ formatCurrency(commission.commission_amount) }}</p>
                                    <p class="rfc-li-status">{{ commission.status }}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Top Referrals -->
                    <div class="rf-card">
                        <div class="rfc-head">
                            <div>
                                <h3 class="rfc-title">{{ t('topReferrals') }}</h3>
                                <p class="rfc-sub">{{ t('yourHighestEarners') }}</p>
                            </div>
                            <Link href="/user/referral-list" class="rfc-pill-btn">
                                {{ t('viewAll') }}
                                <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"/>
                                </svg>
                            </Link>
                        </div>
                        <div class="rfc-list">
                            <div v-for="referral in topReferrals" :key="referral.id" class="rfc-list-item">
                                <div class="rfc-li-avatar">{{ getInitials(referral.name) }}</div>
                                <div class="rfc-li-info">
                                    <p class="rfc-li-name">{{ referral.name }}</p>
                                    <p class="rfc-li-meta">{{ t('level') }} {{ referral.level }}</p>
                                </div>
                                <div class="rfc-li-right">
                                    <p class="rfc-li-amount">{{ currencySymbol }}{{ formatCurrency(referral.total_commission_earned) }}</p>
                                    <p class="rfc-li-status">{{ t('earned') }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </UserLayout>
</template>

<script setup>
import {ref, computed, onMounted} from 'vue';
import { usePage, Link} from '@inertiajs/vue3';
import UserLayout from "@/Layouts/UserLayout/UserLayout.vue";
import {useToast} from "@/composables/useToast.js";
import {useTranslation} from '@/composables/useTranslation';

const props = defineProps({
    referralStats: {
        type: Object,
        required: true
    },
    commissionTiers: {
        type: Array,
        default: () => []
    },
    recentCommissions: {
        type: Array,
        default: () => []
    },
    topReferrals: {
        type: Array,
        default: () => []
    },
    referralLink: {
        type: String,
        required: true
    },
    referralCode: {
        type: String,
        required: true
    }
});

const {showToast} = useToast();
const {t} = useTranslation();
const page = usePage();

const copied = ref(false);
const progressGoal = computed(() => {
    const earned = props.referralStats.totalEarned;
    if (earned < 5000) return 5000;
    if (earned < 10000) return 10000;
    if (earned < 20000) return 20000;
    if (earned < 50000) return 50000;
    return Math.ceil(earned / 10000) * 10000 + 10000;
});

const primaryColor = computed(() => page.props.primaryColor || '#86efac');
const currencySymbol = computed(() => page.props.currencySymbol || '$');

const hexToRgb = (hex) => {
    if (!hex || typeof hex !== 'string') {
        return {r: 134, g: 239, b: 172};
    }
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : {r: 134, g: 239, b: 172};
};

const rgbToHex = (r, g, b) => {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

const adjustColor = (hex, amount) => {
    const rgb = hexToRgb(hex);
    const newR = Math.min(255, Math.max(0, rgb.r + amount));
    const newG = Math.min(255, Math.max(0, rgb.g + amount));
    const newB = Math.min(255, Math.max(0, rgb.b + amount));
    return rgbToHex(newR, newG, newB);
};

const themeColors = computed(() => ({
    primary: primaryColor.value,
    primaryDark: adjustColor(primaryColor.value, -20),
    primaryLight: adjustColor(primaryColor.value, 40),
    primaryRgb: `${hexToRgb(primaryColor.value).r}, ${hexToRgb(primaryColor.value).g}, ${hexToRgb(primaryColor.value).b}`
}));

const progressPercentage = computed(() => {
    return Math.min(100, Math.round((props.referralStats.totalEarned / progressGoal.value) * 100));
});

const formatCurrency = (amount) => {
    const number = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(number);
};

const formatNumber = (num) => {
    const number = parseFloat(num) || 0;
    if (number >= 1000000000) {
        return (number / 1000000000).toFixed(1) + 'B';
    } else if (number >= 1000000) {
        return (number / 1000000).toFixed(1) + 'M';
    } else if (number >= 1000) {
        return (number / 1000).toFixed(1) + 'K';
    }
    return number.toString();
};

const calculateGrowth = () => {
    return props.referralStats.monthlyGrowth || 0;
};

const getInitials = (name) => {
    if (!name) return 'NA';
    const parts = name.split(' ');
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
};

const copyToClipboard = async () => {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(props.referralLink);
            copied.value = true;
            showToast(t('referralLinkCopied') || 'Referral link copied!', 'success');
            setTimeout(() => {
                copied.value = false;
            }, 2000);
        } else {
            fallbackCopyToClipboard(props.referralLink);
        }
    } catch (err) {
        console.error('Failed to copy:', err);
        fallbackCopyToClipboard(props.referralLink);
    }
};

const copyReferralCode = async () => {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(props.referralCode);
            showToast(t('referralCodeCopied') || 'Referral code copied!', 'success');
        } else {
            fallbackCopyToClipboard(props.referralCode);
        }
    } catch (err) {
        console.error('Failed to copy:', err);
        fallbackCopyToClipboard(props.referralCode);
    }
};

const fallbackCopyToClipboard = (text) => {
    try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        const successful = document.execCommand('copy');
        textArea.remove();

        if (successful) {
            if (text === props.referralLink) {
                copied.value = true;
                setTimeout(() => {
                    copied.value = false;
                }, 2000);
            }
            showToast(
                text === props.referralLink
                    ? (t('referralLinkCopied') || 'Referral link copied!')
                    : (t('referralCodeCopied') || 'Referral code copied!'),
                'success'
            );
        } else {
            throw new Error('Copy command was unsuccessful');
        }
    } catch (err) {
        console.error('Fallback copy failed:', err);
        showToast(t('failedToCopy') || 'Failed to copy. Please copy manually.', 'error');
    }
};

const selectText = (event) => {
    event.target.select();
};

onMounted(() => {
    try {
        const flash = page.props.flash;
        if (flash?.success) showToast(flash.success, 'success');
        if (flash?.error) showToast(flash.error, 'error');
        if (flash?.warning) showToast(flash.warning, 'warning');
        if (flash?.info) showToast(flash.info, 'info');
    } catch (error) {
        console.error('Component mount error:', error);
    }
});
</script>

<style scoped>
.anim-up { animation: slideUp 0.45s cubic-bezier(0.22,1,0.36,1) both; animation-delay: var(--d,0ms); }
@keyframes slideUp { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:none} }

.rf-wrap { position:relative; z-index:10; width:100%; }
.rf-inner { max-width:1280px; margin:0 auto; padding:14px 12px; display:flex; flex-direction:column; gap:12px; }
@media(min-width:640px)  { .rf-inner { padding:18px 18px; gap:14px; } }
@media(min-width:1024px) { .rf-inner { padding:20px 24px; gap:16px; } }

/* Welcome */
.rf-welcome {
    display:flex; align-items:center; justify-content:space-between;
    gap:12px; flex-wrap:wrap;
    padding:12px 16px; border-radius:13px;
    background:rgba(167,93,237,0.06);
    border:1px solid rgba(167,93,237,0.16);
}
.rw-left { display:flex; align-items:center; gap:12px; }
.rw-icon {
    width:40px; height:40px; border-radius:11px;
    background:linear-gradient(135deg,rgba(124,58,237,0.3),rgba(167,93,237,0.2));
    border:1px solid rgba(167,93,237,0.28);
    display:flex; align-items:center; justify-content:center;
    color:#a75ded; flex-shrink:0;
    box-shadow:0 0 14px rgba(167,93,237,0.22);
}
.rw-eyebrow { font-size:9px; font-weight:700; color:rgba(167,93,237,0.7); text-transform:uppercase; letter-spacing:0.1em; margin:0 0 1px; }
.rw-title { font-size:15px; font-weight:900; color:#fff; letter-spacing:-0.02em; margin:0; }
.rw-chips { display:flex; gap:7px; flex-wrap:wrap; }
.rw-chip {
    display:flex; flex-direction:column; align-items:center; gap:2px;
    padding:6px 12px; border-radius:9px;
    background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07);
    min-width:58px;
}
.rw-chip--green { background:rgba(52,211,153,0.06); border-color:rgba(52,211,153,0.14); }
.rw-chip--green .rw-chip-val { color:#34d399 !important; }
.rw-chip--amber { background:rgba(251,191,36,0.06); border-color:rgba(251,191,36,0.14); }
.rw-chip--amber .rw-chip-val { color:#fbbf24 !important; }
.rw-chip-val { font-size:13px; font-weight:900; color:#fff; }
.rw-chip-lbl { font-size:8px; font-weight:700; color:#4b5563; text-transform:uppercase; letter-spacing:0.12em; }

/* Stats */
.rf-stats { display:grid; grid-template-columns:repeat(2,1fr); gap:10px; }
@media(min-width:768px) { .rf-stats { grid-template-columns:repeat(4,1fr); } }

.rf-stat { background:rgba(10,4,22,0.97); border:1px solid rgba(var(--ac),0.17); border-radius:16px; padding:14px; position:relative; overflow:hidden; }
.rf-stat::before { content:''; position:absolute; top:0; left:10%; right:10%; height:1px; background:linear-gradient(90deg,transparent,rgba(var(--ac),0.4),transparent); }
.rst-glow { position:absolute; top:-18px; right:-18px; width:70px; height:70px; border-radius:50%; background:radial-gradient(circle,rgba(var(--ac),0.16) 0%,transparent 70%); pointer-events:none; }
.rst-icon { width:30px; height:30px; border-radius:8px; background:rgba(var(--ac),0.13); border:1px solid rgba(var(--ac),0.22); display:flex; align-items:center; justify-content:center; color:rgb(var(--ac)); margin-bottom:9px; }
.rst-lbl { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.07em; color:rgba(255,255,255,0.4); margin:0 0 3px; }
.rst-val { font-size:20px; font-weight:800; color:#fff; margin:0 0 2px; line-height:1.1; }
.rst-sub { font-size:11px; font-weight:600; color:rgb(var(--ac)); margin:0 0 8px; }
.rst-bar-wrap { height:3px; background:rgba(255,255,255,0.06); border-radius:99px; overflow:hidden; }
.rst-bar { height:3px; border-radius:99px; background:rgba(var(--ac),0.65); }

/* Shimmer */
.shimmer-bar { position:relative; overflow:hidden; }
.shimmer-bar::after { content:''; position:absolute; inset:0; background:linear-gradient(90deg,transparent 0%,rgba(255,255,255,0.18) 50%,transparent 100%); animation:shimmer 2s infinite; }
@keyframes shimmer { from{transform:translateX(-100%)} to{transform:translateX(100%)} }

/* Main 2-col */
.rf-main { display:grid; gap:12px; }
@media(min-width:900px) { .rf-main { grid-template-columns:1fr 340px; align-items:start; } }
@media(min-width:1200px) { .rf-main { grid-template-columns:1fr 360px; } }

.rf-left,.rf-right { display:flex; flex-direction:column; gap:12px; }

/* Card base */
.rf-card {
    background:rgba(10,4,22,0.97);
    border:1px solid rgba(167,93,237,0.13);
    border-radius:16px;
    padding:14px;
    position:relative; overflow:hidden;
    transition:border-color 0.25s;
    display:flex; flex-direction:column; gap:10px;
}
.rf-card::before { content:''; position:absolute; top:0; left:14%; right:14%; height:1px; background:linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent); }
.rf-card:hover { border-color:rgba(167,93,237,0.24); }
@media(min-width:640px) { .rf-card { padding:16px; gap:11px; } }
.rf-card--stretch { flex:1; }

.rfc-head { display:flex; align-items:flex-start; justify-content:space-between; gap:10px; margin-bottom:8px; }
.rfc-title { font-size:13px; font-weight:800; color:#fff; letter-spacing:-0.01em; }
.rfc-sub { font-size:10px; color:#374151; margin-top:1px; }

.rfc-pill-btn { display:inline-flex; align-items:center; gap:4px; font-size:11px; font-weight:600; padding:6px 11px; border-radius:8px; background:rgba(167,93,237,0.1); border:1px solid rgba(167,93,237,0.26); color:#a75ded; text-decoration:none; white-space:nowrap; transition:opacity .2s; flex-shrink:0; }
.rfc-pill-btn:hover { opacity:.78; }

.rfc-link-row { display:flex; gap:8px; }
.rfc-input { flex:1; min-width:0; background:rgba(255,255,255,0.04); border:1px solid rgba(167,93,237,0.17); border-radius:10px; padding:10px 14px; font-size:12px; font-family:monospace; color:rgba(255,255,255,0.65); outline:none; transition:border-color .2s; }
.rfc-input:focus { border-color:rgba(167,93,237,0.45); }
.rfc-copy-btn { display:inline-flex; align-items:center; gap:5px; padding:10px 15px; border-radius:10px; background:linear-gradient(135deg,#a75ded,#8845cc); color:#fff; font-size:12px; font-weight:700; border:none; cursor:pointer; white-space:nowrap; flex-shrink:0; transition:opacity .2s; }
.rfc-copy-btn:hover { opacity:.85; }

.rfc-tiers { display:grid; grid-template-columns:repeat(auto-fill,minmax(68px,1fr)); gap:8px; }
.rfc-tier { background:rgba(167,93,237,0.07); border:1px solid rgba(167,93,237,0.14); border-radius:10px; padding:12px 6px; text-align:center; transition:background .2s; }
.rfc-tier:hover { background:rgba(167,93,237,0.13); }
.rfc-tier-rate { font-size:19px; font-weight:800; color:#a75ded; margin-bottom:4px; }
.rfc-tier-lvl { font-size:11px; font-weight:600; color:#fff; margin-bottom:2px; }
.rfc-tier-type { font-size:10px; color:rgba(255,255,255,0.38); text-transform:capitalize; }

.rfc-mini-stats { display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-bottom:14px; }
.rfc-mini-stat { background:rgba(167,93,237,0.08); border:1px solid rgba(167,93,237,0.14); border-radius:10px; padding:12px; text-align:center; }
.rfc-mini-val { font-size:22px; font-weight:800; color:#fff; margin:0 0 2px; }
.rfc-mini-lbl { font-size:10px; font-weight:600; color:#a75ded; margin:0; text-transform:uppercase; letter-spacing:.05em; }

.rfc-prog-block { margin-bottom:14px; }
.rfc-prog-head { display:flex; justify-content:space-between; margin-bottom:7px; }
.rfc-prog-lbl { font-size:11px; color:rgba(255,255,255,0.42); }
.rfc-prog-earned { font-size:12px; font-weight:700; color:#fff; }
.rfc-prog-track { height:5px; background:rgba(255,255,255,0.07); border-radius:99px; overflow:hidden; margin-bottom:5px; }
.rfc-prog-fill { height:5px; border-radius:99px; background:linear-gradient(90deg,#a75ded,#8845cc); transition:width 1s ease; }
.rfc-prog-marks { display:flex; justify-content:space-between; font-size:10px; color:rgba(255,255,255,0.32); }

.rfc-code-block { background:rgba(167,93,237,0.08); border:1px solid rgba(167,93,237,0.18); border-radius:12px; padding:13px; }
.rfc-code-label { font-size:9px; font-weight:600; text-transform:uppercase; letter-spacing:.08em; color:rgba(255,255,255,0.36); margin:0 0 8px; }
.rfc-code-row { display:flex; align-items:center; gap:8px; }
.rfc-code { flex:1; background:rgba(0,0,0,0.28); border-radius:7px; padding:8px 11px; font-size:12px; font-family:monospace; font-weight:700; color:#a75ded; word-break:break-all; }
.rfc-code-btn { width:34px; height:34px; border-radius:8px; background:rgba(167,93,237,0.16); border:1px solid rgba(167,93,237,0.28); color:#a75ded; display:flex; align-items:center; justify-content:center; cursor:pointer; flex-shrink:0; transition:background .2s; }
.rfc-code-btn:hover { background:rgba(167,93,237,0.28); }

/* Bottom */
.rf-bottom { display:grid; gap:12px; }
@media(min-width:768px) { .rf-bottom { grid-template-columns:1fr 1fr; } }

.rfc-list { display:flex; flex-direction:column; gap:7px; }
.rfc-list-item { display:flex; align-items:center; gap:10px; padding:11px 12px; background:rgba(255,255,255,0.025); border:1px solid rgba(167,93,237,0.07); border-radius:11px; transition:background .2s; }
.rfc-list-item:hover { background:rgba(167,93,237,0.07); }
.rfc-li-icon,.rfc-li-avatar { width:34px; height:34px; border-radius:9px; background:rgba(167,93,237,0.13); border:1px solid rgba(167,93,237,0.2); display:flex; align-items:center; justify-content:center; color:#a75ded; flex-shrink:0; font-size:11px; font-weight:800; }
.rfc-li-info { flex:1; min-width:0; }
.rfc-li-name { font-size:13px; font-weight:600; color:#fff; margin:0 0 2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.rfc-li-meta { font-size:11px; color:rgba(255,255,255,0.38); margin:0; }
.rfc-li-right { text-align:right; flex-shrink:0; }
.rfc-li-amount { font-size:13px; font-weight:700; color:#a75ded; margin:0 0 2px; }
.rfc-li-status { font-size:10px; color:rgba(255,255,255,0.38); margin:0; text-transform:capitalize; }

@media(prefers-reduced-motion:reduce) {
    .anim-up { animation:none; }
    .shimmer-bar::after { animation:none; }
}
</style>
